import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Upload, Plus, Save, Edit, Trash2, Move, Eye, EyeOff, Image as ImageIcon } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { ImageWithFallback } from '../../components/ui/ImageWithFallback';
import { toast } from 'sonner';
import companyParameterService from '../../services/companyParameterService';

type Category = {
  id: string;
  _id?: string;
  name: string;
  slug: string;
  icon_url?: string;
  description?: string;
  is_scroll?: boolean;
  scroll_order?: number;
};

export function CategoryManagement() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [industries, setIndustries] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCat, setNewCat] = useState<Partial<Category>>({ name: '', slug: '', is_scroll: false, scroll_order: 0 });
  const [newCatIconFile, setNewCatIconFile] = useState<File | null>(null);
  const [newCatIconPreview, setNewCatIconPreview] = useState<string | null>(null);
  const [editing, setEditing] = useState<Category | null>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);

  const sorted = useMemo(() => {
    const list = [...categories];
    return list.sort((a, b) => {
      const ao = Number(a.scroll_order || 0);
      const bo = Number(b.scroll_order || 0);
      if ((a.is_scroll ? 0 : 1) !== (b.is_scroll ? 0 : 1)) return (a.is_scroll ? 0 : 1) - (b.is_scroll ? 0 : 1);
      if (ao !== bo) return ao - bo;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }, [categories]);

  const load = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.getCategories();
      const items = Array.isArray(res?.categories) ? res.categories : Array.isArray(res) ? res : [];
      setCategories(items);
    } catch { }
    setLoading(false);
  };

  const loadIndustries = async () => {
    try {
      const res: any = await companyParameterService.list('industry');
      const items = (res.items || res || []).map((i: any) => ({
        id: i._id || i.id,
        name: i.name,
      }));
      setIndustries(items);
    } catch { }
  };

  useEffect(() => {
    load();
    loadIndustries();
  }, []);

  const toSlug = (s: string) => String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const handleCreate = async () => {
    if (!newCat.name) return;
    setCreating(true);
    try {
      const provided = String(newCat.slug || '').trim();
      const validKebab = /^[-a-z0-9]+$/.test(provided);
      const slug = validKebab ? provided : toSlug(String(newCat.name || ''));
      const created = await apiClient.createCategory({
        name: String(newCat.name),
        slug,
        icon_url: newCat.icon_url,
        description: newCat.description,
        is_scroll: !!newCat.is_scroll,
        scroll_order: Number(newCat.scroll_order || 0)
      } as any);
      const item = (created as any)?.id ? (created as any) : created;
      let finalItem = item;
      try {
        if (newCatIconFile) {
          const id = String(item._id || item.id || '');
          const res = await apiClient.uploadCategoryIcon(id, newCatIconFile);
          const url = (res as any)?.icon_url || (res as any)?.category?.icon_url || '';
          finalItem = { ...item, icon_url: url };
        }
      } catch { }
      setCategories(prev => [...prev, finalItem]);
      setNewCat({ name: '', slug: '', is_scroll: false, scroll_order: 0 });
      setNewCatIconFile(null);
      setNewCatIconPreview(null);
      toast.success('Category added successfully');
    } catch (err: any) {
      const msg = String(err?.message || 'Failed to add category');
      toast.error(msg);
    } finally { setCreating(false); }
  };

  const handleUpdate = async () => {
    if (!editing) return;
    setLoading(true);
    try {
      const id = String(editing._id || editing.id || '');
      const updated = await apiClient.updateCategory(id, {
        name: editing.name,
        slug: editing.slug,
        icon_url: editing.icon_url,
        description: editing.description,
        is_scroll: !!editing.is_scroll,
        scroll_order: Number(editing.scroll_order || 0)
      });
      setCategories(prev => prev.map(c => (String(c.id || c._id) === String(id) ? (updated as any) : c)));
      setEditing(null);
    } catch { } finally { setLoading(false); }
  };

  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      await apiClient.deleteCategory(id);
      setCategories(prev => prev.filter(c => String(c.id || c._id) !== String(id)));
    } catch { } finally { setLoading(false); }
  };

  const toggleScroll = async (id: string, next: boolean) => {
    setLoading(true);
    try {
      const updated = await apiClient.setCategoryScroll(id, { is_scroll: next });
      setCategories(prev => prev.map(c => (String(c.id || c._id) === String(id) ? (updated as any) : c)));
    } catch { } finally { setLoading(false); }
  };

  const moveOrder = async (id: string, dir: 'up' | 'down') => {
    const item = categories.find(c => String(c.id || c._id) === String(id));
    if (!item) return;
    const delta = dir === 'up' ? -1 : 1;
    const next = Number(item.scroll_order || 0) + delta;
    setLoading(true);
    try {
      const updated = await apiClient.setCategoryScroll(id, { is_scroll: !!item.is_scroll, scroll_order: next });
      setCategories(prev => prev.map(c => (String(c.id || c._id) === String(id) ? (updated as any) : c)));
    } catch { } finally { setLoading(false); }
  };

  const handleUploadIcon = async (id: string, file: File) => {
    setUploadingId(id);
    try {
      const res = await apiClient.uploadCategoryIcon(id, file);
      const url = (res as any)?.icon_url || (res as any)?.category?.icon_url || '';
      setCategories(prev => prev.map(c => (String(c.id || c._id) === String(id) ? { ...c, icon_url: url } : c)));
    } catch { } finally { setUploadingId(null); }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Browse Job Categories</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name * (Select from Industries)</Label>
              <Select
                value={newCat.name || ''}
                onValueChange={(val) => {
                  const slug = toSlug(val);
                  setNewCat({ ...newCat, name: val, slug });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Industry" />
                </SelectTrigger>
                <SelectContent>
                  {industries.length === 0 && <SelectItem value="no-industries" disabled>No industries found</SelectItem>}
                  {industries.map(ind => (
                    <SelectItem key={ind.id} value={ind.name}>{ind.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Slug *</Label>
              <Input
                value={newCat.slug as any}
                onChange={e => setNewCat({ ...newCat, slug: e.target.value })}
                placeholder="information-technology"
              />
            </div>
            <div className="space-y-2">
              <Label>Scroll Order</Label>
              <Input type="number" value={Number(newCat.scroll_order || 0)} onChange={e => setNewCat({ ...newCat, scroll_order: Number(e.target.value || 0) })} />
            </div>
            <div className="space-y-2">
              <Label>Show In Scrolling</Label>
              <Select value={String(!!newCat.is_scroll)} onValueChange={v => setNewCat({ ...newCat, is_scroll: v === 'true' })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Logo</Label>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg border overflow-hidden bg-white">
                  <ImageWithFallback src={newCatIconPreview || 'https://placehold.co/80x80?text=Logo'} alt={String(newCat.name || 'Logo')} className="w-full h-full object-contain" />
                </div>
                <label className="inline-flex items-center gap-2 px-3 py-2 border rounded cursor-pointer">
                  <Upload className="h-4 w-4" />
                  <span>Upload</span>
                  <input type="file" className="hidden" accept="image/*" onChange={e => { const f = e.target.files?.[0]; if (f) { setNewCatIconFile(f); setNewCatIconPreview(URL.createObjectURL(f)); } }} />
                </label>
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={handleCreate} disabled={creating}>
              <Plus className="h-4 w-4 mr-2" />
              {creating ? 'Adding...' : 'Add Category'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Categories ({categories.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {sorted.map(cat => (
              <div key={String(cat._id || cat.id)} className="border rounded-lg p-4">
                {editing && String(editing.id || editing._id) === String(cat.id || cat._id) ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Select
                        value={editing.name || ''}
                        onValueChange={(val) => {
                          const slug = toSlug(val);
                          setEditing({ ...editing, name: val, slug });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Industry" />
                        </SelectTrigger>
                        <SelectContent>
                          {industries.map(ind => (
                            <SelectItem key={ind.id} value={ind.name}>{ind.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Slug *</Label>
                      <Input value={editing.slug} onChange={e => setEditing({ ...editing, slug: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Scroll Order</Label>
                      <Input type="number" value={Number(editing.scroll_order || 0)} onChange={e => setEditing({ ...editing, scroll_order: Number(e.target.value || 0) })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Show In Scrolling</Label>
                      <Select value={String(!!editing.is_scroll)} onValueChange={v => setEditing({ ...editing, is_scroll: v === 'true' })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">Yes</SelectItem>
                          <SelectItem value="false">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Logo</Label>
                      <div className="flex items-center gap-3">
                        {editing.icon_url && (
                          <div className="w-12 h-12 rounded-lg border overflow-hidden">
                            <ImageWithFallback src={editing.icon_url} alt={editing.name} className="w-full h-full object-contain" />
                          </div>
                        )}
                        <label className="inline-flex items-center gap-2 px-3 py-2 border rounded cursor-pointer">
                          <Upload className="h-4 w-4" />
                          <span>Upload</span>
                          <input type="file" className="hidden" accept="image/*" onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadIcon(String(editing._id || editing.id), f); }} />
                        </label>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button variant="default" onClick={handleUpdate} disabled={loading}>
                        <Save className="h-4 w-4 mr-2" />
                        Save
                      </Button>
                      <Button variant="outline" onClick={() => setEditing(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => moveOrder(String(cat._id || cat.id), 'up')} disabled={Number(cat.scroll_order || 0) <= 0}>
                          <Move className="h-4 w-4" />
                        </Button>
                        <span className="text-sm text-gray-500">#{Number(cat.scroll_order || 0)}</span>
                      </div>
                      {cat.icon_url && (
                        <div className="w-12 h-12 rounded-lg border overflow-hidden">
                          <ImageWithFallback src={cat.icon_url} alt={cat.name} className="w-full h-full object-contain" />
                        </div>
                      )}
                      <div>
                        <div className="font-semibold text-lg">{cat.name}</div>
                        <div className="text-sm text-gray-500">{cat.slug}</div>
                      </div>
                      <Badge variant={cat.is_scroll ? 'default' : 'secondary'}>{cat.is_scroll ? 'Scrolling' : 'Hidden'}</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => toggleScroll(String(cat._id || cat.id), !cat.is_scroll)} disabled={loading}>
                        {cat.is_scroll ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setEditing(cat)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(String(cat._id || cat.id))} className="text-red-600 hover:text-red-700 border-red-200">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {sorted.length === 0 && (
              <div className="text-center text-gray-600">No categories yet</div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default CategoryManagement;
